"use-strict";
var themeStorageFallback = Object.create(null);
var themeStoragePending = new Set();

function getThemeStorageItem(key) {
  if (themeStoragePending.has(key)) return themeStorageFallback[key] ?? null;
  try {
    const value = window.localStorage.getItem(key);
    if (value === null) delete themeStorageFallback[key];
    else themeStorageFallback[key] = value;
    return value;
  } catch {
    return themeStorageFallback[key] ?? null;
  }
}

function setThemeStorageItem(key, value) {
  themeStorageFallback[key] = String(value);
  try {
    window.localStorage.setItem(key, value);
    themeStoragePending.delete(key);
    return true;
  } catch {
    themeStoragePending.add(key);
    return false;
  }
}

function removeThemeStorageItem(key) {
  delete themeStorageFallback[key];
  try {
    window.localStorage.removeItem(key);
    themeStoragePending.delete(key);
  } catch {
    themeStoragePending.add(key);
  }
}

function parseThemeStorageArray(value) {
  try {
    const parsed = JSON.parse(value || "[]");
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.filter((item) =>
      typeof item === "string" || (typeof item === "number" && Number.isFinite(item))
    ).map(String).filter(Boolean))];
  } catch {
    return [];
  }
}

function readThemeStorageArray(key) {
  return parseThemeStorageArray(getThemeStorageItem(key));
}

function themeRoute(path) {
  return (window.Shopify?.routes?.root || "/") + String(path).replace(/^\/+/, "");
}

function getThemeMessage(key) {
  const defaults = {
    request_error: "We couldn't load this content. Please try again.",
    cart_error: "We couldn't update your cart. Please review your cart before trying again.",
    cart_changed: "Your cart has changed. Please review it before trying again.",
    cart_unconfirmed: "We couldn't confirm this change. Please review your cart before trying again.",
    retry: "Try again",
    view_cart: "View cart",
  };
  return window.themeRecoveryStrings?.[key] || defaults[key] || defaults.request_error;
}

function clearThemeRequestError(container) {
  container?.querySelectorAll(".theme-request-status").forEach((node) => node.remove());
}

function showThemeRequestError(container, message, options = {}) {
  if (!container) return;
  clearThemeRequestError(container);
  const status = document.createElement("div");
  status.className = "theme-request-status form__message error p-15 my-15 border rounded-5";
  status.setAttribute("role", "alert");
  const text = document.createElement("p");
  text.className = "my-0";
  text.textContent = message || getThemeMessage("request_error");
  status.append(text);
  if (typeof options.retry === "function") {
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "btn-outline mt-10";
    retry.textContent = getThemeMessage("retry");
    retry.addEventListener("click", async () => {
      retry.disabled = true;
      try { await options.retry(); }
      finally { retry.disabled = false; }
    });
    status.append(retry);
  }
  if (options.cartLink) {
    const link = document.createElement("a");
    link.href = window.routes?.cart_url || themeRoute("cart");
    link.className = "inline-block mt-10 underline";
    link.textContent = getThemeMessage("view_cart");
    status.append(link);
  }
  container.append(status);
}

async function fetchThemeContent(url, options, format) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, {credentials: "same-origin", ...options, signal: controller.signal});
    const data = await (format === "json" ? response.json() : response.text());
    return {response, data};
  } finally {
    window.clearTimeout(timer);
  }
}

async function fetchThemeJSON(url, options = {}) {
  let result;
  try { result = await fetchThemeContent(url, options, "json"); }
  catch { throw new Error(getThemeMessage("cart_unconfirmed")); }
  const {response, data} = result;
  if (!response.ok || Number(data?.status) >= 400 || data?.errors) {
    const description = typeof data?.description === "string" ? data.description :
      typeof data?.errors === "string" ? data.errors : getThemeMessage("cart_error");
    const error = new Error(description);
    error.payload = data;
    error.status = response.status;
    throw error;
  }
  return data;
}

async function fetchThemeProductSections(ids, sectionId, rootSelector) {
  if (!sectionId) throw new Error(getThemeMessage("request_error"));
  const cleanIds = [...new Set(ids.map(String).filter((id) => /^\d+$/.test(id)))];
  const results = [];
  for (let start = 0; start < cleanIds.length; start += 40) {
    const url = new URL(window.routes?.search_url || themeRoute("search"), window.location.origin);
    url.searchParams.set("section_id", sectionId);
    url.searchParams.set("type", "product");
    url.searchParams.set("options[unavailable_products]", "last");
    url.searchParams.set("q", cleanIds.slice(start, start + 40).map((id) => "id:" + id).join(" OR "));
    const {response, data} = await fetchThemeContent(url.pathname + url.search, {}, "text");
    if (!response.ok) throw new Error(getThemeMessage("request_error"));
    const doc = new DOMParser().parseFromString(data, "text/html");
    const root = doc.querySelector(rootSelector);
    if (!root) throw new Error(getThemeMessage("request_error"));
    results.push(root);
  }
  return results;
}

function getThemeCartSections(cart) {
  const sections = cart?.getSectionsToRender?.() || [];
  const main = document.getElementById("main-cart-items");
  if (document.body.classList.contains("template-cart") && main) {
    sections.push({id: "main-cart-items", section: main.dataset.id || "main-cart-items", selector: ".js-contents"});
  }
  return sections.filter((section, index) => sections.findIndex((other) =>
    (other.section || other.id) === (section.section || section.id)) === index).slice(0, 5);
}

function themeCartSectionParams(sections) {
  return {sections: sections.map((section) => section.section || section.id), sections_url: window.location.pathname};
}

async function getThemeCart() {
  const cart = await fetchThemeJSON(themeRoute("cart.js"), {cache: "no-store"});
  if (!Array.isArray(cart?.items) || typeof cart.item_count !== "number") throw new Error(getThemeMessage("cart_unconfirmed"));
  return cart;
}

function updateThemeCartIndicators(cart) {
  if (cart?.item_count === undefined) return;
  document.querySelectorAll(".cart-count").forEach((node) => {
    node.textContent = node.classList.contains("cart-count-drawer") ? `(${cart.item_count})` :
      cart.item_count > 100 ? "~" : String(cart.item_count);
  });
  document.querySelector("header-total-price")?.updateTotal?.(cart);
  document.querySelector("free-ship-progress-bar")?.init?.(cart.items_subtotal_price);
  document.querySelectorAll("cart-notification, cart-drawer").forEach((node) => node.classList.toggle("is-empty", cart.item_count === 0));
}

async function renderThemeCartSections(state, sections) {
  let rendered = state?.sections || {};
  const parse = () => sections.map((section) => {
    const source = rendered[section.section || section.id];
    const wrapper = document.getElementById(section.id);
    const target = section.selector ? wrapper?.querySelector(section.selector) : wrapper;
    if (typeof source !== "string" || !source.trim() || !target) return null;
    const doc = new DOMParser().parseFromString(source, "text/html");
    const content = doc.querySelector(section.selector || "#minicart-form");
    return content ? {target, content, doc} : null;
  });
  let updates = parse();
  if (updates.some((entry) => !entry)) {
    const url = new URL(window.routes?.cart_url || themeRoute("cart"), window.location.origin);
    url.searchParams.set("sections", sections.map((section) => section.section || section.id).join(","));
    try { rendered = await fetchThemeJSON(url.pathname + url.search, {cache: "no-store"}); }
    catch { return false; }
    updates = parse();
  }
  if (updates.some((entry) => !entry)) return false;
  updates.forEach(({target, content}) => { target.innerHTML = content.innerHTML; });
  updates.forEach(({doc}) => {
    const totals = doc.querySelector(".cart-info .totals");
    const currentTotals = document.querySelector(".cart-info .totals");
    if (totals && currentTotals) currentTotals.innerHTML = totals.innerHTML;
  });
  return true;
}

function goToThemeCart() {
  window.location.assign(window.routes?.cart_url || themeRoute("cart"));
}

function themeCartIdentity(item) {
  const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
  const plan = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || "";
  return JSON.stringify([String(item.variant_id || item.id), properties, String(plan)]);
}

function findThemeCartLine(cart, item) {
  const exact = item.key ? cart.items.find((line) => line.key === item.key) : null;
  if (exact) return exact;
  const candidates = cart.items.filter((line) => themeCartIdentity(line) === themeCartIdentity(item));
  return candidates.length === 1 ? candidates[0] : null;
}

var themeCartEditBusy = false;

async function replaceThemeCartLine(form, lineKey, sections) {
  if (themeCartEditBusy) throw new Error(getThemeMessage("cart_changed"));
  themeCartEditBusy = true;
  const post = (path, data) => fetchThemeJSON(themeRoute(path), {...fetchConfig(), body: JSON.stringify(data)});
  try {
    const before = await getThemeCart();
    if (!Array.isArray(before.items)) throw new Error(getThemeMessage("cart_error"));
    const old = before.items.find((item) => item.key === lineKey);
    if (!old) throw new Error(getThemeMessage("cart_changed"));
    const fields = new FormData(form);
    const quantity = fields.has("quantity") ? Number(fields.get("quantity")) : old.quantity;
    const variant = String(fields.get("id") || "");
    if (!/^\d+$/.test(variant) || !Number.isInteger(quantity) || quantity < 1) throw new Error(getThemeMessage("cart_error"));
    const properties = {...(old.properties || {})};
    for (const [key, value] of fields.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === "string") properties[match[1]] = value;
    }
    const plan = fields.has("selling_plan") ? fields.get("selling_plan") : old.selling_plan_allocation?.selling_plan?.id;
    const desired = {id: variant, variant_id: variant, quantity, properties, selling_plan: plan || ""};
    const params = themeCartSectionParams(sections);
    if (themeCartIdentity(old) === themeCartIdentity(desired)) {
      return await post("cart/change.js", {id: old.key, quantity, ...params});
    }
    if (before.items.filter((line) => themeCartIdentity(line) === themeCartIdentity(old)).length > 1) throw new Error(getThemeMessage("cart_changed"));
    const matchingBefore = before.items.filter((item) => themeCartIdentity(item) === themeCartIdentity(desired));
    if (matchingBefore.length > 1) throw new Error(getThemeMessage("cart_changed"));
    const baseline = matchingBefore[0]?.quantity || 0;
    const recoverAddition = async (error) => {
      try {
        const latest = await getThemeCart();
        const original = findThemeCartLine(latest, old);
        const replacement = findThemeCartLine(latest, desired);
        if (original?.quantity !== old.quantity) {
          error.cart = latest;
          error.uncertain = true;
          return;
        }
        const currentQuantity = replacement?.quantity || 0;
        if (currentQuantity === baseline) {
          error.cart = latest;
          error.uncertain = false;
        } else if (currentQuantity === baseline + quantity) {
          const restored = await post("cart/change.js", {id: replacement.key, quantity: baseline, ...params});
          if (!Array.isArray(restored?.items) || findThemeCartLine(restored, old)?.quantity !== old.quantity ||
            (findThemeCartLine(restored, desired)?.quantity || 0) !== baseline) throw new Error(getThemeMessage("cart_unconfirmed"));
          error.cart = restored;
          error.restored = true;
          error.uncertain = false;
        } else {
          error.cart = latest;
          error.uncertain = true;
        }
      } catch {
        error.uncertain = true;
      }
    };
    const item = {id: variant, quantity, properties};
    if (plan) item.selling_plan = plan;
    let added;
    try { added = await post("cart/add.js", {items: [item]}); }
    catch (error) {
      await recoverAddition(error);
      if (error.uncertain) error.message = getThemeMessage("cart_unconfirmed");
      throw error;
    }
    const addedItem = added.items?.[0] || added;
    let afterAdd;
    try { afterAdd = await getThemeCart(); }
    catch {
      const error = new Error(getThemeMessage("cart_unconfirmed"));
      error.uncertain = true;
      throw error;
    }
    const oldCurrent = findThemeCartLine(afterAdd, old);
    const newCurrent = findThemeCartLine(afterAdd, {...desired, key: addedItem.key});
    if (!oldCurrent || oldCurrent.quantity !== old.quantity || !newCurrent || newCurrent.quantity !== baseline + quantity) {
      const error = new Error(getThemeMessage("cart_changed"));
      error.cart = afterAdd;
      error.uncertain = true;
      throw error;
    }
    try {
      const changed = await post("cart/change.js", {id: oldCurrent.key, quantity: 0, ...params});
      if (!Array.isArray(changed?.items) || findThemeCartLine(changed, oldCurrent)) throw new Error(getThemeMessage("cart_changed"));
      return changed;
    } catch (error) {
      try {
        const latest = await getThemeCart();
        const remainingOld = findThemeCartLine(latest, oldCurrent);
        const remainingNew = findThemeCartLine(latest, newCurrent);
        if (!remainingOld && remainingNew?.quantity === baseline + quantity) return latest;
        if (remainingOld?.quantity === old.quantity && remainingNew?.quantity === baseline + quantity) {
          error.cart = await post("cart/change.js", {id: remainingNew.key, quantity: baseline, ...params});
          if (!Array.isArray(error.cart?.items) || findThemeCartLine(error.cart, old)?.quantity !== old.quantity ||
            (findThemeCartLine(error.cart, desired)?.quantity || 0) !== baseline) throw new Error(getThemeMessage("cart_unconfirmed"));
          error.restored = true;
        } else {
          error.cart = latest;
          error.uncertain = true;
        }
      } catch {
        error.uncertain = true;
      }
      if (error.uncertain) error.message = getThemeMessage("cart_unconfirmed");
      throw error;
    }
  } finally {
    themeCartEditBusy = false;
  }
}

function getCookie(cname) {
  const name = cname + "=";
  for (const part of document.cookie.split(";")) {
    const cookie = part.trim();
    if (cookie.startsWith(name)) {
      const value = cookie.substring(name.length);
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }
  return "";
}

function deleteCookie(cname) {
  document.cookie = cname + "=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;SameSite=Lax";
}

function setCookie(cname, cvalue, exdays) {
  const date = new Date();
  date.setTime(date.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + date.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/;SameSite=Lax" + (window.location.protocol === "https:" ? ";Secure" : "");
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

let parser = new DOMParser();

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  cartError: "cart-error",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
};

let subscribers = {};
var root = document.body;

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target?.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

Shopify.formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";
    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }
  return formatString.replace(placeholderRegex, value);
};
var getScrollBarWidth = (function () {
  return {
    init: function () {
      var scrollDiv = document.createElement("div");
      scrollDiv.style.width = "100px";
      scrollDiv.style.height = "100px";
      scrollDiv.style.overflow = "scroll";
      scrollDiv.style.position = "absolute";
      scrollDiv.style.top = "-9999px";
      document.body.appendChild(scrollDiv);
      var scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
      document.body.removeChild(scrollDiv);
      return scrollbarWidth;
    },
  };
})();
function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      if (data) {
        callback(data);
      } else {
        callback();
      }
    });
  }
}
function initLazyloadItem() {
  const lazyVideos = document.querySelectorAll("video.lazy-video-item");

  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const video = entry.target;
          const poster = video.dataset.poster;
          const src = video.dataset.src;

          if (poster) {
            video.setAttribute("poster", poster);
            video.removeAttribute("data-poster");
            video.setAttribute("src", src);
            video.removeAttribute("data-src");
          }

          video.querySelectorAll("source").forEach((source) => {
            const source_src = source.dataset.src;
            if (source_src) {
              source.setAttribute("src", source_src);
              source.removeAttribute("data-src");
            }
          });

          video.classList.remove("lazy-video-item");
          observer.unobserve(video); // Stop observing once loaded
        }
      });
    },
    { threshold: 0.1 } // Trigger when 10% of the video is visible
  );

  lazyVideos.forEach((video) => observer.observe(video));
}

// Initialize lazy loading item
initLazyloadItem();

function initLazyload() {
  window.addEventListener("scroll", () => {
    scrollLazyload();
  });
  let pos = window.pageYOffset;
  if (pos > 40) {
    scrollLazyload();
  }
}
initLazyload();

function scrollLazyload() {
  document.querySelectorAll("video.lazy-video").forEach((el) => {
    scrollLazyloadItem(el);
  });
}

function scrollLazyloadItem(el) {
  const poster = el.dataset.poster;
  const src = el.dataset.src;
  if (poster) {
    el.setAttribute("poster", poster);
    el.removeAttribute("data-poster");
    el.setAttribute("src", src);
    el.removeAttribute("data-src");
  }
  el.querySelectorAll("source").forEach((source) => {
    const source_src = source.dataset.src;
    if (source_src) {
      source.setAttribute("src", source_src);
      source.removeAttribute("data-src");
    }
  });
  el.classList.remove("lazy-video");
}
